/**
 * Step 6: Beneficial Ownership & Directors
 * Collects beneficial ownership and director information
 * Supports NAVI AI document analysis
 */

import React, { useState } from 'react';
import { Users, Robot, Upload, Check, X, Warning, Eye, Trash, Plus } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TextField, SelectField, ToggleField, TextAreaField,
  StepSection, CollapsibleSection, DynamicList, FileUpload, InfoCard 
} from './SharedComponents';
import { API } from '@/App';

const COUNTRIES = [
  'South Africa', 'Namibia', 'Botswana', 'Zimbabwe', 'Mozambique', 
  'United Kingdom', 'United States', 'Mauritius', 'British Virgin Islands',
  'Cayman Islands', 'Netherlands', 'Luxembourg', 'Other'
];

const emptyBeneficialOwner = () => ({
  id: crypto.randomUUID(),
  name: '',
  person_or_entity: 'natural_person',
  registration_or_id: '',
  country: 'South Africa',
  ownership_percentage: '',
  voting_percentage: '',
  control_rights: '',
  ownership_type: 'direct',
  rationale: '',
  navi_extracted: false,
});

const emptyDirector = () => ({
  id: crypto.randomUUID(),
  first_name: '',
  surname: '',
  identification_number: '',
  passport_number: '',
  nationality: 'South African',
  date_of_appointment: '',
  is_pep: false,
  is_pip: false,
  risk_considerations: '',
  navi_extracted: false,
});

// NAVI Analysis Component
const NaviAnalysisSection = ({ formData, updateField, token }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  const handleDocumentUpload = async (file, type) => {
    if (!file) return;
    
    // In production, you would upload the file to storage first
    // For now, we'll simulate the URL
    const documentUrl = URL.createObjectURL(file);
    
    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/intake-wizard/v2/questionnaire/analyze-document`,
        null,
        {
          params: {
            document_url: documentUrl,
            document_type: type,
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAnalysisResult(response.data);
      
      if (response.data.analysis_status === 'completed') {
        toast.success('Document analyzed successfully. Please review the extracted data.');
      } else {
        toast.warning('Analysis completed with warnings. Manual review recommended.');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze document. Please enter data manually.');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const handleApproveAnalysis = async (approved) => {
    if (!analysisResult?.id) return;
    
    try {
      await axios.post(
        `${API}/intake-wizard/v2/questionnaire/approve-navi-analysis`,
        null,
        {
          params: {
            analysis_id: analysisResult.id,
            approved,
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (approved) {
        // Update form with extracted data
        if (analysisResult.beneficial_owners?.length > 0) {
          updateField('beneficial_owners', analysisResult.beneficial_owners.map(bo => ({
            ...emptyBeneficialOwner(),
            ...bo,
            navi_extracted: true,
          })));
        }
        if (analysisResult.executive_directors?.length > 0) {
          updateField('executive_directors', analysisResult.executive_directors.map(d => ({
            ...emptyDirector(),
            ...d,
            navi_extracted: true,
          })));
        }
        if (analysisResult.non_executive_directors?.length > 0) {
          updateField('non_executive_directors', analysisResult.non_executive_directors.map(d => ({
            ...emptyDirector(),
            ...d,
            navi_extracted: true,
          })));
        }
        
        toast.success('NAVI analysis approved and data saved.');
      } else {
        toast.info('NAVI analysis rejected. Please enter data manually.');
      }
      
      setAnalysisResult(null);
    } catch (error) {
      console.error('Approval error:', error);
      toast.error('Failed to process approval.');
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Robot size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">NAVI Document Analysis</h3>
            <p className="text-xs text-text-secondary">Upload a document and let NAVI extract beneficial ownership data</p>
          </div>
        </div>
        
        {!analysisResult ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FileUpload
              label="Organogram"
              onUpload={(file) => handleDocumentUpload(file, 'organogram')}
              accept=".pdf,.png,.jpg,.jpeg"
              uploading={analyzing}
              helpText="Corporate structure chart"
              testId="organogram-upload"
            />
            <FileUpload
              label="JSE Share Register"
              onUpload={(file) => handleDocumentUpload(file, 'jse_register')}
              accept=".pdf,.xlsx,.xls"
              uploading={analyzing}
              helpText="Shareholder register"
              testId="jse-register-upload"
            />
            <FileUpload
              label="CIPC Declaration"
              onUpload={(file) => handleDocumentUpload(file, 'cipc_declaration')}
              accept=".pdf,.png,.jpg"
              uploading={analyzing}
              helpText="Beneficial ownership declaration"
              testId="cipc-declaration-upload"
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Analysis Results Preview */}
            <div className="p-4 bg-background-surface rounded-lg border border-border">
              <h4 className="text-sm font-medium text-text-primary mb-3">
                Analysis Results - {analysisResult.analysis_status}
              </h4>
              
              {analysisResult.warnings?.length > 0 && (
                <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-1">
                    <Warning size={16} />
                    Warnings
                  </div>
                  <ul className="text-xs text-text-secondary list-disc list-inside">
                    {analysisResult.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="space-y-2 text-sm">
                <p className="text-text-secondary">
                  <strong>Beneficial Owners:</strong> {analysisResult.beneficial_owners?.length || 0} identified
                </p>
                <p className="text-text-secondary">
                  <strong>Executive Directors:</strong> {analysisResult.executive_directors?.length || 0} identified
                </p>
                <p className="text-text-secondary">
                  <strong>Non-Executive Directors:</strong> {analysisResult.non_executive_directors?.length || 0} identified
                </p>
              </div>
            </div>
            
            {/* Approval Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => handleApproveAnalysis(true)}
                className="flex-1 py-2 px-4 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
              >
                <Check size={18} />
                Approve & Use Data
              </button>
              <button
                onClick={() => handleApproveAnalysis(false)}
                className="flex-1 py-2 px-4 bg-background-surface border border-border text-text-secondary rounded-lg font-medium hover:border-red-400 hover:text-red-400 transition-colors flex items-center justify-center gap-2"
              >
                <X size={18} />
                Reject & Enter Manually
              </button>
            </div>
          </div>
        )}
      </div>
      
      <InfoCard variant="info">
        A human must review and approve NAVI's analysis. If rejected, please capture the data manually below.
      </InfoCard>
    </div>
  );
};

// Beneficial Owner Form
const BeneficialOwnerForm = ({ owner, index, onUpdate, onRemove }) => (
  <div className="space-y-3">
    {owner.navi_extracted && (
      <div className="text-xs text-primary flex items-center gap-1">
        <Robot size={12} />
        Extracted by NAVI
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <TextField
        label="Name"
        value={owner.name}
        onChange={(v) => onUpdate(index, 'name', v)}
        placeholder="Person or entity name"
        required
      />
      <SelectField
        label="Type"
        value={owner.person_or_entity}
        onChange={(v) => onUpdate(index, 'person_or_entity', v)}
        options={[
          { id: 'natural_person', name: 'Natural Person' },
          { id: 'legal_entity', name: 'Legal Entity' },
        ]}
      />
      <TextField
        label="ID/Registration Number"
        value={owner.registration_or_id}
        onChange={(v) => onUpdate(index, 'registration_or_id', v)}
        placeholder={owner.person_or_entity === 'natural_person' ? 'ID Number' : 'Registration Number'}
      />
      <SelectField
        label="Country"
        value={owner.country}
        onChange={(v) => onUpdate(index, 'country', v)}
        options={COUNTRIES}
      />
      <TextField
        label="Ownership %"
        type="number"
        value={owner.ownership_percentage}
        onChange={(v) => onUpdate(index, 'ownership_percentage', v)}
        placeholder="e.g., 25"
      />
      <TextField
        label="Voting %"
        type="number"
        value={owner.voting_percentage}
        onChange={(v) => onUpdate(index, 'voting_percentage', v)}
        placeholder="e.g., 25"
      />
      <SelectField
        label="Ownership Type"
        value={owner.ownership_type}
        onChange={(v) => onUpdate(index, 'ownership_type', v)}
        options={[
          { id: 'direct', name: 'Direct' },
          { id: 'indirect', name: 'Indirect' },
        ]}
      />
      <TextField
        label="Control Rights"
        value={owner.control_rights}
        onChange={(v) => onUpdate(index, 'control_rights', v)}
        placeholder="e.g., Board appointment rights"
      />
    </div>
    <TextAreaField
      label="Rationale"
      value={owner.rationale}
      onChange={(v) => onUpdate(index, 'rationale', v)}
      placeholder="Why is this person/entity identified as a beneficial owner?"
      rows={2}
    />
  </div>
);

// Director Form
const DirectorForm = ({ director, index, onUpdate, isExecutive }) => (
  <div className="space-y-3">
    {director.navi_extracted && (
      <div className="text-xs text-primary flex items-center gap-1">
        <Robot size={12} />
        Extracted by NAVI
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <TextField
        label="First Name"
        value={director.first_name}
        onChange={(v) => onUpdate(index, 'first_name', v)}
        placeholder="First name"
        required
      />
      <TextField
        label="Surname"
        value={director.surname}
        onChange={(v) => onUpdate(index, 'surname', v)}
        placeholder="Surname"
        required
      />
      <TextField
        label="ID Number"
        value={director.identification_number}
        onChange={(v) => onUpdate(index, 'identification_number', v)}
        placeholder="SA ID Number"
      />
      <TextField
        label="Passport (if foreign)"
        value={director.passport_number}
        onChange={(v) => onUpdate(index, 'passport_number', v)}
        placeholder="Passport number"
      />
      <TextField
        label="Nationality"
        value={director.nationality}
        onChange={(v) => onUpdate(index, 'nationality', v)}
        placeholder="Nationality"
      />
      <TextField
        label="Date of Appointment"
        type="date"
        value={director.date_of_appointment}
        onChange={(v) => onUpdate(index, 'date_of_appointment', v)}
      />
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <ToggleField
        label="Politically Exposed Person (PEP)"
        checked={director.is_pep || false}
        onChange={(v) => onUpdate(index, 'is_pep', v)}
      />
      <ToggleField
        label="Prominent Influential Person (PIP)"
        checked={director.is_pip || false}
        onChange={(v) => onUpdate(index, 'is_pip', v)}
      />
    </div>
    
    {(director.is_pep || director.is_pip) && (
      <TextAreaField
        label="Risk Considerations"
        value={director.risk_considerations}
        onChange={(v) => onUpdate(index, 'risk_considerations', v)}
        placeholder="Document any risk considerations for this PEP/PIP..."
        rows={2}
      />
    )}
  </div>
);

const Step6BeneficialOwnership = ({ formData, updateField, token }) => {
  const updateBeneficialOwner = (index, key, value) => {
    const current = [...(formData.beneficial_owners || [])];
    current[index] = { ...current[index], [key]: value };
    updateField('beneficial_owners', current);
  };
  
  const addBeneficialOwner = () => {
    const current = formData.beneficial_owners || [];
    updateField('beneficial_owners', [...current, emptyBeneficialOwner()]);
  };
  
  const removeBeneficialOwner = (index) => {
    const current = [...(formData.beneficial_owners || [])];
    current.splice(index, 1);
    updateField('beneficial_owners', current);
  };
  
  const updateDirector = (type, index, key, value) => {
    const field = type === 'executive' ? 'executive_directors' : 'non_executive_directors';
    const current = [...(formData[field] || [])];
    current[index] = { ...current[index], [key]: value };
    updateField(field, current);
  };
  
  const addDirector = (type) => {
    const field = type === 'executive' ? 'executive_directors' : 'non_executive_directors';
    const current = formData[field] || [];
    updateField(field, [...current, emptyDirector()]);
  };
  
  const removeDirector = (type, index) => {
    const field = type === 'executive' ? 'executive_directors' : 'non_executive_directors';
    const current = [...(formData[field] || [])];
    current.splice(index, 1);
    updateField(field, current);
  };
  
  return (
    <StepSection
      title="Beneficial Ownership & Directors"
      description="Capture beneficial owners and director information"
      icon={Users}
    >
      {/* NAVI Analysis Section */}
      <NaviAnalysisSection
        formData={formData}
        updateField={updateField}
        token={token}
      />
      
      {/* Beneficial Owners */}
      <CollapsibleSection title="Beneficial Owners" defaultOpen testId="beneficial-owners-section">
        <DynamicList
          items={formData.beneficial_owners || []}
          onAdd={addBeneficialOwner}
          onRemove={removeBeneficialOwner}
          addButtonText="Add Beneficial Owner"
          testId="beneficial-owners-list"
          renderItem={(owner, idx) => (
            <BeneficialOwnerForm
              owner={owner}
              index={idx}
              onUpdate={updateBeneficialOwner}
              onRemove={() => removeBeneficialOwner(idx)}
            />
          )}
        />
        
        {/* Ownership Total Warning */}
        {formData.beneficial_owners?.length > 0 && (
          <div className="mt-4">
            {(() => {
              const total = (formData.beneficial_owners || []).reduce(
                (sum, bo) => sum + (parseFloat(bo.ownership_percentage) || 0), 0
              );
              if (total < 95) {
                return (
                  <InfoCard variant="warning">
                    Total ownership is {total.toFixed(1)}%. Some ownership may be missing.
                  </InfoCard>
                );
              } else if (total > 105) {
                return (
                  <InfoCard variant="warning">
                    Total ownership is {total.toFixed(1)}%. Please verify percentages.
                  </InfoCard>
                );
              }
              return null;
            })()}
          </div>
        )}
      </CollapsibleSection>
      
      {/* Executive Directors */}
      <CollapsibleSection title="Executive Directors" testId="exec-directors-section">
        <DynamicList
          items={formData.executive_directors || []}
          onAdd={() => addDirector('executive')}
          onRemove={(idx) => removeDirector('executive', idx)}
          addButtonText="Add Executive Director"
          testId="exec-directors-list"
          renderItem={(director, idx) => (
            <DirectorForm
              director={director}
              index={idx}
              onUpdate={(i, k, v) => updateDirector('executive', i, k, v)}
              isExecutive={true}
            />
          )}
        />
      </CollapsibleSection>
      
      {/* Non-Executive Directors */}
      <CollapsibleSection title="Non-Executive Directors" testId="non-exec-directors-section">
        <DynamicList
          items={formData.non_executive_directors || []}
          onAdd={() => addDirector('non-executive')}
          onRemove={(idx) => removeDirector('non-executive', idx)}
          addButtonText="Add Non-Executive Director"
          testId="non-exec-directors-list"
          renderItem={(director, idx) => (
            <DirectorForm
              director={director}
              index={idx}
              onUpdate={(i, k, v) => updateDirector('non-executive', i, k, v)}
              isExecutive={false}
            />
          )}
        />
      </CollapsibleSection>
    </StepSection>
  );
};

export default Step6BeneficialOwnership;
